import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BaseComponent } from '../core/base.component';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { HeaderService } from '../core/services/header.service';
import { Title } from '@angular/platform-browser';
import { AuthService } from '../core/security/auth.service';
import { MessageBroadcastingService } from './message-broadcasting.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { FileHelpers } from '../core/utilities/file.helpers';
import { NotificationService } from '../notification/notification.service';
import { CurrentUser } from '../api/models/current-user';
import { ModulesEnum } from '../core/security/modules.enum';
import { RolesEnum } from '../core/security/roles.enum';
import { MatDialog } from '@angular/material/dialog';
import { MatConfirmDialogComponent } from '../dialogs/mat-confirm/mat-confirm-dialog.component';
import { BroadcastPhoneNumberGroupComponent } from './phone-number-group/broadcast-phone-number-group.component';
import { MatDrawerContainer, MatDrawerMode, MatDrawer, MatDrawerContent } from '@angular/material/sidenav';
import { LocalNotificationsService } from '../core/services/local-notifications.service';
import { SaveBroadcastThreadDialogComponent } from './save-broadcast-thread/save-broadcast-thread-dialog.component';
import { BroadcastThreadViewModel } from '../api/models/broadcast-thread-view-model';
import { QueueBroadcastMessageV2Model } from '../api/models/queue-broadcast-message-v-2-model';
import { BroadcastMessagePreviewDialogComponent } from './broadcast-message-preview/broadcast-message-preview-dialog.component';
import { UnsubscribeListDialogComponent } from './unsubscribe-list-dialog/unsubscribe-list-dialog.component';
import { BillingCycleLimitModel } from '../api/models/billing-cycle-limit-model';
import { ZendeskService } from '../core/services/zendesk.service';
import { ProMessagingService } from '../pro-messaging/pro-messaging.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { BroadcastThreadContactsCountResponse } from '../api/models/broadcast-thread-contacts-count-response';
import { NgIf, NgFor, NgClass } from '@angular/common';
import { MatCard, MatCardHeader, MatCardContent } from '@angular/material/card';
import { MessagesModule } from 'primeng/messages';
import { PrimeTemplate } from 'primeng/api';
import { FlexModule } from '@angular/flex-layout';
import { MatIconButton, MatButton, MatIconAnchor } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldFix } from '../editors/mat-form-field/mat-form-field-fix';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatBadge } from '@angular/material/badge';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { BroadcastMessagesGridComponent } from './messages-grid/broadcast-messages-grid.component';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { SelectTemplateV2Component } from '../pro-messaging-template/select-template-v2/select-template-v2.component';
import { DroppableDirective } from '../core/visual/droppable/droppable.directive';
import { PhoneNumberPipe } from '../core/pipes/phone-number.pipe';
import { Message } from 'primeng/message';

@Component({
  selector: 'app-message-broadcasting',
  templateUrl: './message-broadcasting.component.html',
  styleUrls: ['./message-broadcasting.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    MatCard,
    MatCardHeader,
    MatCardContent,
    MessagesModule,
    RouterLink,
    MatDrawerContainer,
    MatDrawer,
    FlexModule,
    MatIconButton,
    MatIcon,
    MatFormFieldFix,
    MatFormField,
    MatSelect,
    FormsModule,
    MatSelectTrigger,
    NgFor,
    MatOption,
    MatBadge,
    MatButton,
    MatPrefix,
    MatInput,
    MatIconAnchor,
    MatSuffix,
    BroadcastPhoneNumberGroupComponent,
    MatDrawerContent,
    ReactiveFormsModule,
    NgClass,
    MatProgressSpinner,
    MatTooltip,
    BroadcastMessagesGridComponent,
    CdkTextareaAutosize,
    MatMenuTrigger,
    SelectTemplateV2Component,
    MatMenu,
    MatMenuItem,
    DroppableDirective,
    PhoneNumberPipe,
    Message,
  ],
})
export class MessageBroadcastingComponent extends BaseComponent implements OnInit, OnDestroy {
  private readonly TAG = '[MessageBroadcastingComponent]';

  @ViewChild('mainContainer') mainContainer: MatDrawerContainer;
  @ViewChild('msgMenu') msgMenu: ElementRef;
  @ViewChild('textInput') textInput: ElementRef;
  @ViewChild('fileInput', { static: false }) fileInput: any;
  @ViewChild('broadcastPhoneNumberGroup') broadcastPhoneNumberGroup: BroadcastPhoneNumberGroupComponent;

  searchTextChanged: Subject<string> = new Subject<string>();

  selectedGroupSubscription: Subscription;
  newMessageSubscription: Subscription;
  routedPhoneNumber: string;
  routedBroadcastId: string;
  selectedGroup: BroadcastThreadViewModel;
  form: UntypedFormGroup;
  submitting = false;
  readOnlyPhone = true;
  buttonSubmitPromise: Promise<void>;
  uncompletedMessages: { [number: string]: string | undefined } = {};

  filesToUpload: File[] = [];
  previewUrl: string;
  previewFileName: string;

  hasContactsModule = false;
  hasContactsViewRight = false;
  hasPhoneNumbersEditRight = false;
  searchTextInternal = '';
  searchText = '';
  hasTemplateAccess = false;
  private paramsSubscription: Subscription;

  limitsReached = false;
  messagingLimits: BillingCycleLimitModel = {
    limits: 0,
    currentUsage: 0,
    billingCycleStart: '',
    currentBillingCycleStart: '',
    currentBillingCycleEnd: '',
  };

  sidenavMode: MatDrawerMode = 'side';
  showThreads = true;
  isMobileView = false;
  mobileViewSubscription: Subscription;
  containerHeight: '100%';
  loadingContactsTotal = false;
  contactsCountCache: Map<string, { timestamp: Date; data: BroadcastThreadContactsCountResponse }> = new Map();

  constructor(
    route: ActivatedRoute,
    headerService: HeaderService,
    titleService: Title,
    authService: AuthService,
    private changeDetectorService: ChangeDetectorRef,
    public notificationService: NotificationService,
    public notificationsService: LocalNotificationsService,
    public zendeskService: ZendeskService,
    public dialog: MatDialog,
    public messagingService: ProMessagingService,
    public messageBroadcastingService: MessageBroadcastingService,
    private fb: UntypedFormBuilder,
    private router: Router,
  ) {
    super(route, headerService, titleService, authService);

    this.mobileViewSubscription = this.headerService.isMobileView.subscribe(value => {
      this.isMobileView = value;
      this.showThreads = !value;
      this.sidenavMode = value ? 'over' : 'side';
    });

    this.paramsSubscription = this.route.queryParams.subscribe(params => {
      this.onRouteParamsChange(params).catch(reason => {
        console.error(reason);
      });
    });

    this.deleteRoles = [RolesEnum.ProMessaging_Delete];
    this.editRoles = [RolesEnum.ProMessaging_Edit, RolesEnum.ProMessaging_Delete];

    this.createForm();
  }

  async onRouteParamsChange(params: Params): Promise<void> {
    console.log(`${this.TAG} onRouteParamsChange`, params);

    this.routedBroadcastId = params['broadcast'];

    if (!this.routedPhoneNumber || this.routedPhoneNumber !== params['tn']) {
      this.routedPhoneNumber = params['tn'];

      if (this.routedPhoneNumber) {
        await this.messageBroadcastingService.messagingPhoneNumbers(this.routedPhoneNumber, true);
      } else {
        await this.messageBroadcastingService.messagingPhoneNumbers(undefined, true);

        const defaultMessagingNumber = this.messageBroadcastingService.getMessagingNumber();
        if (defaultMessagingNumber) {
          this.navigateToThread(defaultMessagingNumber).catch(reason => {
            console.error(reason);
          });
        }
      }

      const messagingNumber = this.messageBroadcastingService.selectedMessagingNumber;
      if (messagingNumber && !messagingNumber.allowSend) {
        this.headerService.addGlobalStaticMessage({
          key: 'sms_read_only_warning',
          severity: 'warn',
          summary: 'View only access.',
          detail: 'To send a message, please contact your admin to update your role with View/Edit permission.',
        });
      } else {
        this.headerService.removeGlobalStaticMessage('sms_read_only_warning');
      }

      this.headerService.addGlobalClosableMessage({
        key: 'sms_broadcast_limit_warning',
        severity: 'warn',
        summary: 'Attention',
        detail:
          'Broadcast Messaging should be limited to 1,000 contacts per message to avoid being marked as spam by receiving carriers.',
      });
    }
  }

  async navigateToThread(tn: string, threadKey?: string) {
    console.log(`${this.TAG} navigateToThread`, tn, threadKey);
    await this.router.navigate([], {
      queryParams: {
        tn: tn,
        broadcast: threadKey,
      },
    });
  }

  onPhoneNumberValueChange(event) {
    console.log(event);

    this.navigateToThread(event.phoneNumber).catch(reason => {
      console.error(reason);
    });
  }

  onCurrentUserChanged(user: CurrentUser): void {
    if (user) {
      this.hasContactsModule = this.authService.userHasPackage([
        ModulesEnum.ContactModule,
        ModulesEnum.PrivateContactModule,
      ]);
      this.hasContactsViewRight = this.authService.hasAccess([
        RolesEnum.CompanyContact_View,
        RolesEnum.CompanyContact_Edit,
        RolesEnum.CompanyContact_Delete,
        RolesEnum.PrivateContact_View,
        RolesEnum.PrivateContact_Edit,
        RolesEnum.PrivateContact_Delete,
      ]);

      this.hasPhoneNumbersEditRight = this.authService.hasAccess([
        RolesEnum.PhoneNumbers_Edit,
        RolesEnum.PhoneNumbers_Delete,
      ]);

      this.hasTemplateAccess = this.authService.hasAccess([
        RolesEnum.MessagingTemplateView,
        RolesEnum.MessagingTemplateEdit,
        RolesEnum.MessagingTemplateDelete,
      ]);
    } else {
      this.hasContactsModule = false;
      this.hasContactsViewRight = false;
      this.hasPhoneNumbersEditRight = false;
      this.hasTemplateAccess = false;
    }
  }

  ngOnInit() {
    super.ngOnInit();

    this.onDeleteConversationProcess = this.onDeleteConversationProcess.bind(this);

    this.newMessageSubscription = this.messageBroadcastingService.onNewMessageSubject
      .pipe(debounceTime(3000))
      .subscribe(async message => {
        // get new billing limits
        await this.updateLimitsMessage();
      });

    this.searchTextChanged.pipe(debounceTime(300), distinctUntilChanged()).subscribe(searchText => {
      this.searchText = searchText;
    });

    this.updateLimitsMessage().then();
  }

  onSelectedThreadChanged(thread: BroadcastThreadViewModel) {
    console.log(`${this.TAG} onSelectedThreadChanged`, thread);
    this.storeTypedMessage();

    if (!this.routedBroadcastId || this.routedBroadcastId !== thread?.id) {
      this.router
        .navigate([], { queryParams: { tn: this.routedPhoneNumber, broadcast: thread?.id } })
        .catch(error => console.log(error));
    }

    this.updateMessagingForm(thread);
  }

  updateMessagingForm(thread: BroadcastThreadViewModel) {
    console.log(`${this.TAG} updateMessagingForm`, thread);
    this.selectedGroup = thread;

    if (this.selectedGroup) {
      this.getContactsCount();
    }

    this.resetForm();
  }

  async updateLimitsMessage() {
    console.log('updateLimitsMessage');
    this.messagingLimits = await this.messagingService.getMessagingLimits();
    if (this.messagingLimits.limits > 0) {
      this.limitsReached = this.messagingLimits.currentUsage >= this.messagingLimits.limits;

      const remainCount = this.messagingLimits.limits - this.messagingLimits.currentUsage;
      const limit10Percent = this.messagingLimits.limits * 0.1;

      if (limit10Percent >= remainCount) {
        this.headerService.addGlobalStaticMessage({
          key: 'pro_sms_limit_warning',
          severity: 'warn',
          summary: 'Warning!',
          detail: `You ${this.limitsReached ? 'have reached' : 'are approaching'} your monthly outgoing SMS message limit for your Pro SMS account.
          You have <b>${remainCount}</b> outgoing messages remaining out of <b>${this.messagingLimits.limits}</b>.
          <a href="javascript:void(0);" class="open-support"><b>Upgrade to Pro</b></a>`,
        });
        setTimeout(() => {
          const supportLink: HTMLAnchorElement = document
            .getElementsByClassName('open-support')
            .item(0) as HTMLAnchorElement;
          console.log('supportLink', supportLink);
          if (supportLink) {
            supportLink.onclick = () => {
              this.zendeskService.show();
              return false;
            };
          }
        });
      } else {
        this.headerService.removeGlobalStaticMessage('pro_sms_limit_warning');
        this.limitsReached = false;
      }
    } else {
      this.headerService.removeGlobalStaticMessage('pro_sms_limit_warning');
      this.limitsReached = false;
    }
  }

  storeTypedMessage() {
    if (this.selectedGroup && this.selectedGroup.id && this.form.value.message) {
      const key = `${this.messageBroadcastingService.getMessagingNumber()}|${this.selectedGroup.id}`;
      this.uncompletedMessages[key] = this.form.value.message;
    }
  }

  clearTypedMessage() {
    if (this.selectedGroup && this.selectedGroup.id && this.form.value.message) {
      const key = `${this.messageBroadcastingService.getMessagingNumber()}|${this.selectedGroup.id}`;
      this.uncompletedMessages[key] = undefined;
    }
  }

  resetForm() {
    const selectedGroup = this.selectedGroup;

    this.readOnlyPhone = selectedGroup && !!selectedGroup.id;

    this.removeAttachment();

    const clientNumbers = [];
    let uncompletedMessage = '';
    if (selectedGroup) {
      const key = `${this.messageBroadcastingService.getMessagingNumber()}|${this.selectedGroup.id}`;
      uncompletedMessage = this.uncompletedMessages[key] || '';
    }

    this.form.reset({
      primaryPhoneNumber: this.messageBroadcastingService.getMessagingNumber(),
      clientPhoneNumbers: clientNumbers,
      message: uncompletedMessage,
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.headerService.removeGlobalStaticMessage('sms_read_only_warning');
    this.headerService.removeGlobalStaticMessage('pro_sms_limit_warning');
    this.headerService.removeGlobalClosableMessage('sms_broadcast_limit_warning');
    this.selectedGroupSubscription?.unsubscribe();
    this.newMessageSubscription?.unsubscribe();
    this.paramsSubscription?.unsubscribe();
    this.mobileViewSubscription?.unsubscribe();
    this.messageBroadcastingService.messagingNumbers = [];
  }

  msgKeyPress(event) {
    if (event.keyCode === 13 && !event.shiftKey) {
      event.preventDefault();

      this.previewBroadcastMessage();
    }
  }

  onNewBroadcast(event) {
    event.preventDefault();
    this.editBroadcast('new');
  }

  fileChangeEvent(fileInput: any) {
    const files = fileInput.target.files;
    if (files.length > 0) {
      const file: File = files[0];
      this.validateAndProceedFile(file);
      this.textInput.nativeElement.focus();
    }
  }

  onFilePaste(event: ClipboardEvent) {
    for (let i = 0; i < event.clipboardData.items.length; i++) {
      const item = event.clipboardData.items[i];
      console.log('Item: ' + item.type);
      if (item.type.indexOf('image') > -1) {
        this.validateAndProceedFile(item.getAsFile());
      } else {
        console.log('Discarding image paste data');
      }
    }
  }

  dropFile(event: DragEvent) {
    console.log('drop event');

    const files = event.dataTransfer.files;

    if (files.length > 0) {
      const file: File = files[0];
      this.validateAndProceedFile(file);
    }
  }

  public validateAndProceedFile(file: File) {
    if (FileHelpers.isSupportedFile(file)) {
      if (FileHelpers.isSupported4MbFileSize(file)) {
        this.filesToUpload = [file];
        this.previewFile(file);
      } else {
        this.notificationService.notifyDanger('File size must be less then 2MB');
      }
    } else {
      this.notificationService.notifyDanger('File type is not supported');
    }
  }

  removeAttachment() {
    this.filesToUpload = [];
    this.previewUrl = null;
    this.previewFileName = null;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  onSubmit() {
    console.log('form submit: ' + this.form.valid);
    if (this.form.valid && !this.submitting) {
      if (!this.readOnlyPhone) {
        this.submitting = true;
        this.buttonSubmitPromise = this.processSubmit();
      } else {
        this.submitting = true;
        this.buttonSubmitPromise = this.processSubmit();
      }
    }
  }

  async processSubmit(): Promise<void> {
    const reqData = this.prepareFormModel();

    if (!reqData.text && !this.filesToUpload[0]) {
      this.submitting = false;
      this.notificationService.notifyDanger('Message is empty');
      return;
    }

    if (reqData.text.length > 2048) {
      this.submitting = false;
      this.notificationService.notifyDanger('Message is too long, max 2048 characters');
      return;
    }

    try {
      if (await this.messageBroadcastingService.sendMessage(reqData, this.filesToUpload[0])) {
        this.clearTypedMessage();
        this.resetForm();
      }
    } catch (e) {
      console.error('unable to send message: ', e);
    } finally {
      this.submitting = false;
    }
  }

  prepareFormModel(): QueueBroadcastMessageV2Model {
    const formModel = this.form.value;

    return {
      broadcastId: this.selectedGroup.id,
      text: this.getFullMessage(formModel.message),
      media: [],
      user: undefined,
    };
  }

  getFullMessage(message: string): string {
    if (this.selectedGroup.unsubscribeCode) {
      return `${message} - unsubscribe code: ${this.selectedGroup.unsubscribeCode}`;
    } else {
      return `${message}`;
    }
  }

  createForm(): void {
    this.form = this.fb.group({
      message: ['', [Validators.maxLength(2048)]],
    });
  }

  clearSearch($event: MouseEvent) {
    this.searchText = '';
    this.searchTextInternal = '';
    this.searchTextChanged.next('');
  }

  onSearchTextChange($event: any) {
    this.searchTextChanged.next($event);
  }

  onTemplateSelected($event: any) {
    console.log($event);
    this.form.patchValue({
      message: $event,
    });
  }

  onDeleteClick() {
    if (this.selectedGroup.id) {
      const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
        // width: '250px',
        data: {
          title: 'Delete Broadcast?',
          text: 'Are you sure you want to delete broadcast?',
          actionTitle: 'Delete',
        },
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.onDeleteConversationProcess(false).catch(error => console.error(error));
        }
      });
    }
  }

  async onDeleteConversationProcess(block: boolean): Promise<void> {
    await this.messageBroadcastingService.deleteFullConversation(this.selectedGroup.id);
    this.notificationService.notifySuccess('Broadcast has been deleted');

    await this.navigateToThread(this.routedPhoneNumber);
  }

  editBroadcast(id: string) {
    const dialogRef = this.dialog.open(SaveBroadcastThreadDialogComponent, {
      width: '650px',
      data: {
        phoneNumber: this.messageBroadcastingService.getMessagingNumber(),
        broadcastId: id,
      },
    });

    dialogRef.afterClosed().subscribe((result: string) => {
      if (result) {
        this.contactsCountCache.delete(this.selectedGroup.id);
        this.broadcastPhoneNumberGroup
          .forceRefresh()
          .catch(reason => {
            console.error(reason);
          })
          .then(() => {
            this.navigateToThread(this.routedPhoneNumber, result).catch(reason => {
              console.error(reason);
            });
          });
      }
    });
  }

  private previewFile(file: File) {
    this.previewFileName = file.name;

    if (file.type.indexOf('image') > -1) {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        this.previewUrl = e.target.result;
        this.changeDetectorService.markForCheck();
      };

      reader.readAsDataURL(file);
    }
  }

  previewBroadcastMessage() {
    const dialogRef = this.dialog.open(BroadcastMessagePreviewDialogComponent, {
      width: '650px',
      data: {
        totalContacts: this.selectedGroup.totalContacts + this.selectedGroup.totalTagContacts,
        message: this.getFullMessage(this.form.value.message),
        previewFileName: this.previewFileName,
        previewUrl: this.previewUrl,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.onSubmit();
      }
    });
  }

  showUnsubscribedList() {
    this.dialog.open(UnsubscribeListDialogComponent, {
      width: '90%',
      height: '90%',
      data: {
        phoneNumber: this.selectedGroup.primaryNumber,
      },
    });
  }

  onThreadsMenuOpened() {
    window.dispatchEvent(new Event('resize'));
  }

  private getContactsCount() {
    const cache = this.contactsCountCache.get(this.selectedGroup.id);
    // If cache is available and not expired (less than 1 minute old), use it
    const now = new Date();
    const oneMinuteAgo = new Date(now.getTime() - 60 * 1000); // 1 minute ago
    if (cache && cache.timestamp > oneMinuteAgo) {
      this.applyContactsCount(cache.data);
    } else {
      this.loadingContactsTotal = true;
      this.messageBroadcastingService.getContactsCount(this.selectedGroup.id).then(contactsCountResponse => {
        // Cache the new data
        this.contactsCountCache.set(this.selectedGroup.id, { timestamp: new Date(), data: contactsCountResponse });
        this.applyContactsCount(contactsCountResponse);
        this.loadingContactsTotal = false;
      });
    }
  }

  private applyContactsCount(contactsCountResponse: any) {
    // Apply data to selected group
    this.selectedGroup.totalContacts = contactsCountResponse.contactsCount;
    this.selectedGroup.totalTagContacts = contactsCountResponse.totalContactsCount;
    this.selectedGroup.totalTags = contactsCountResponse.tagsCount;
  }
}
